import { faPlay } from '@fortawesome/pro-regular-svg-icons/faPlay';
import {
  ContestResult,
  ContestState
} from '@maxpreps/apiserviceclients/apiModels';
import { publicOrigin } from '@maxpreps/network/src/origins';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import Anchor from '@maxpreps/shared/corecomponents/Anchor/Anchor';
import useHasMounted from '@maxpreps/shared/hooks/useHasMounted';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { useIsMobile } from '@maxpreps/shared/hooks/useIsScreenSize';
import { useUserRoles } from '@maxpreps/shared/redux/user/userHooks';
import { MediaQuery } from '@maxpreps/shared/styles/breakpoint';
import THEME from '@maxpreps/shared/styles/colors';
import Font from '@maxpreps/shared/styles/font';
import Icon from '@maxpreps/shared/ui/Icon/Icon';
import StyledText from '@maxpreps/shared/ui/Text/StyledText';
import styled from 'styled-components';
import getContestResultColor from '../../../utils/schedule/getContestResultColor';
import {
  IClientContest,
  IClientContestTeam
} from '../../../utils/schedule/serializeSchedule';
import userCanEnterScores from '../../../utils/schedule/userCanEnterScores';
import useIsUserATeamAdmin from '../../../utils/useIsUserATeamAdmin';
import useScheduleContext from '../ScheduleContext';
import getIsPublishedSportSeason from './../../../utils/getIsPublishedSportSeason';
import {
  getAdminEnterScoresLink,
  getAdminEnterStatsLink,
  getPublicReportScoreUrl
} from './../../../utils/navigation';

const getResultsLink = (
  canonicalUrl: string,
  contestId: string,
  teamId: string,
  allSeasonId: string,
  ssid: string,
  isAdmin: boolean
) => {
  if (!isAdmin) return canonicalUrl;

  return getAdminEnterScoresLink(teamId, allSeasonId, ssid, contestId);
};

const getScoreDisplay = (
  currentTeam: IClientContestTeam,
  opponentTeam: IClientContestTeam
) => {
  // Show forfeit text rather than score if team won/lost on forfeit
  if (
    currentTeam.calculatedTeamContestResult === ContestResult.LossByForfeit ||
    currentTeam.calculatedTeamContestResult === ContestResult.WinByForfeit
  ) {
    return '(FF)';
  }

  if (currentTeam.calculatedTeamContestResult === ContestResult.DoubleForfeit) {
    return '(DFF)';
  }

  const { score } = currentTeam;
  const { score: opponentScore } = opponentTeam;

  // Show highest score first
  return (score ?? 0) > (opponentScore ?? 0)
    ? `${score}-${opponentScore}`
    : `${opponentScore}-${score}`;
};

export const getReportScoreLink = (
  contestId: string,
  teamId: string,
  allSeasonId: string,
  ssid: string,
  isAdmin: boolean
) => {
  if (isAdmin) {
    return getAdminEnterScoresLink(teamId, allSeasonId, ssid, contestId);
  }

  return getPublicReportScoreUrl(contestId, ssid);
};

// This control would be improved if it forked out each ContestState
// button state into it's own control. It'd remove lots of overhead
// where we are account for every state in this implementation
const ResultsActionButton: React.FC<{
  contest: IClientContest;
}> = ({ contest }) => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const teamContext = useTeamContext();
  const roles = useUserRoles();
  const hasMounted = useHasMounted();
  const isUserATeamAdmin = useIsUserATeamAdmin();

  const {
    contestId,
    hasContestPage,
    currentTeam,
    canonicalUrl,
    contestState,
    rolesWhoCanEnterScores,
    reasonWhyCannotEnterScores
  } = contest;

  const { data } = teamContext;
  const { teamId, allSeasonId, sportSeasonId } = data;
  const { hasStats, calculatedTeamContestResult } = currentTeam;

  const reportScoreLink = hasMounted
    ? getReportScoreLink(
        contestId,
        teamId,
        allSeasonId,
        sportSeasonId,
        isAdmin || isUserATeamAdmin
      )
    : undefined;

  const canEnterScore = userCanEnterScores(
    roles,
    teamId,
    allSeasonId,
    rolesWhoCanEnterScores,
    reasonWhyCannotEnterScores
  );

  const enterStatsAdminLink = getAdminEnterStatsLink(
    teamId,
    allSeasonId,
    sportSeasonId,
    contestId
  );

  const isForfeit =
    calculatedTeamContestResult === ContestResult.WinByForfeit ||
    calculatedTeamContestResult === ContestResult.LossByForfeit ||
    calculatedTeamContestResult === ContestResult.DoubleForfeit;

  let link: undefined | string = undefined;
  let text: undefined | string = undefined;

  switch (contestState) {
    case ContestState.ScoreNotReported:
    case ContestState.ContestInProgress:
      if (canEnterScore && reportScoreLink) {
        text = isAdmin ? 'Enter Score' : 'Report Score';
        link = reportScoreLink;
      }
      break;
    case ContestState.Boxscore:
      if (isAdmin && !isForfeit && !hasStats) {
        text = 'Enter Stats';
        link = enterStatsAdminLink;
        break;
      }

      text = 'Box Score';
      link = hasContestPage ? canonicalUrl : undefined;
      break;
    case ContestState.Pregame:
      text = 'Preview';
      link = hasContestPage ? canonicalUrl : undefined;
      break;
  }

  return (
    (text && link && (
      <Anchor href={link} className={isMobile ? Font.f12_tall : Font.f14_tall}>
        {text}
      </Anchor>
    )) ||
    null
  );
};

const StyledContestResults = styled(Anchor)`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  /* Align bold text with normal text */
  & > :first-child {
    position: relative;
    bottom: -1px;
    ${MediaQuery.Tablet} {
      bottom: 1px;
    }
  }
`;

const ScheduleResultsCell = ({ contest }) => {
  const isAdmin = useIsAdmin();
  const teamContext = useTeamContext();
  const scheduleContext = useScheduleContext();
  const isMobile = useIsMobile();
  const isUserATeamAdmin = useIsUserATeamAdmin();

  const { data, teamSeasonPickerData } = teamContext;
  const { teamId, sportSeasonId, sport, allSeasonId } = data;

  const {
    contestId,
    hasContestPage,
    hasResult,
    currentTeam,
    opponentTeam,
    canonicalUrl,
    isDeleted,
    overtimePeriodsPlayed,
    overtimeShortAlias
  } = contest;

  if (!getIsPublishedSportSeason(sportSeasonId, teamSeasonPickerData)) {
    return null;
  }

  const overtimeText = overtimePeriodsPlayed
    ? ` (${
        overtimePeriodsPlayed > 1 ? overtimePeriodsPlayed : ''
      }${overtimeShortAlias})`
    : null;

  const shouldShowRecap =
    hasContestPage &&
    hasResult &&
    'football,basketball'.indexOf(sport.toLowerCase()) >= 0;

  const resultsLink = getResultsLink(
    canonicalUrl,
    contestId,
    teamId,
    allSeasonId,
    sportSeasonId,
    isAdmin || isUserATeamAdmin
  );

  // Deleted game should still show score, even if it doesn't have a contest page.
  const showResults =
    (!isDeleted && hasContestPage && hasResult) || (isDeleted && hasResult);

  const correctScore =
    scheduleContext.isCorrectAScoreMode && !isAdmin && contest.hasResult;

  return (
    <td>
      {showResults && (
        <StyledContestResults
          href={resultsLink}
          className={isMobile ? Font.f12_tall : Font.f16_bold_tall}
        >
          <StyledText
            $color={getContestResultColor(currentTeam.result)}
            $marginRight="4px"
            $fontWeight="bold"
          >
            {currentTeam.result}
          </StyledText>
          <StyledText $fontWeight="normal">
            {getScoreDisplay(currentTeam, opponentTeam)}
            {overtimeText && overtimeText}
          </StyledText>
        </StyledContestResults>
      )}
      {correctScore && (
        <Anchor
          $color={THEME.text.alert}
          className={isMobile ? Font.f12_bold_tall : Font.f16_bold_tall}
          href={`${publicOrigin}/utility/reportscore/report_final_score.aspx?contestid=${contest.contestId}&ssid=${contest.sportSeasonId}`}
        >
          Correct Score
        </Anchor>
      )}
      {!correctScore && (
        <>
          <ResultsActionButton contest={contest} />
          {shouldShowRecap && (
            <div>
              <Anchor
                href={canonicalUrl}
                className={isMobile ? Font.f12_tall : Font.f14_tall}
              >
                <Icon size={10} icon={faPlay} /> Recap
              </Anchor>
            </div>
          )}
        </>
      )}
    </td>
  );
};

export default ScheduleResultsCell;
